<script setup>
  import {inject, onBeforeUnmount, ref, toValue} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {
    MedButton,
    MedCheckbox,
    MedFormField,
    MedInfoButton,
    MedLabel,
    MedSpinner,
  } from "@pharma4u/medicheck-components";
  import Window from "@utils/Helpers/Window.js";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";

  defineOptions({
    name: "ComponentBmpExportDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    medications: {
      type: Object,
      required: true,
    },
    laborvalues: {
      type: Object,
      required: true,
    },
    diseases: {
      type: Object,
      required: true,
    },
  });

  const privacy = inject("$privacy");

  const bmpDialog = ref(null);

  const abortController = ref(null);

  const loading = ref(false);
  const error = ref(false);

  const prependMealToInfo = ref(null);
  const splitSectionsBasedOnUsage = ref(null);

  const open = () => {
    prependMealToInfo.value = null;
    splitSectionsBasedOnUsage.value = null;
    abortController.value = null;
    loading.value = false;
    error.value = null;

    bmpDialog.value.open();
  };

  const pushToBmpManager = async () => {
    if (abortController.value) {
      abortController.value.abort();
    }

    abortController.value = new AbortController();

    loading.value = true;
    error.value = false;

    const patient = await privacy.decryptPatient(props.patient);
    const record = await privacy.decryptRecord(props.record);

    if (record.checks) {
      delete record.checks;
    }

    if (record.metadata) {
      delete record.metadata;
    }

    if (record.patient) {
      delete record.patient;
    }

    pdfRouter.initializeBmpManager(
      {
        client: privacy.getClient(),
        patient: patient,
        record: record,
        medications: props.medications?.data ?? [],
        laborvalues: props.laborvalues?.data ?? [],
        diseases: props.diseases?.data ?? [],
      },
      {
        onSuccess: (response) => {
          // add patient id to data.url
          Window.open(response.data.url + "/" + patient.id, "_blank")
            .then(() => close())
            .catch(() => {
              showError("popup");
            });
        },
        onError: () => {
          showError("default");
        },
      },
      {
        signal: abortController.value.signal,
      },
      {
        prependMealToInfo: toValue(prependMealToInfo) ?? false,
        splitSectionsBasedOnUsage: toValue(splitSectionsBasedOnUsage) ?? false,
      },
    );
  };

  const showError = (errorType) => {
    error.value = errorType;
    loading.value = false;
  };

  const close = () => {
    if (abortController.value) {
      abortController.value.abort();
    }

    bmpDialog.value.close();
  };

  onBeforeUnmount(() => {
    close();
  });

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="bmpDialog"
    dialog-title="BMP-Manager"
    :test-id="getTestId('bmp-export-manager')"
    @dialog-closed="close"
  >
    <template #info-icon>
      <MedInfoButton
        size="lg"
        side="left"
        aria-label="BMP-Manager Info"
        disable-portal
        :data-test="getTestId('bmp-export-info-button')"
      >
        <p class="text-sm">
          Im BMP-Manager können Sie den Medikationsplan finalisieren, indem Sie bspw. Rezeptur- oder Freitextzeilen
          hinzufügen oder Indikationen für Ihren Patienten als Laienbegriff eintragen.
        </p>
        <p class="mt-4 text-sm">
          Der Medikationsplan basiert auf den Medikationsdaten der Eingabemaske. Der Mahlzeitenzusammenhang und die
          Medikationsart werden nicht automatisch übernommen, da diese individuell eingetragen werden sollten. Bitte
          verwenden Sie Hinweise zum Mahlzeitenbezug mit möglichst konkreten Zeitangaben und verzichten Sie
          grundsätzlich auf den Hinweis „unabhängig von der Mahlzeit".
        </p>
        <p class="mt-4 text-sm">
          Durch Aktivieren der Checkboxen können Sie diese Angaben dennoch automatisiert übernehmen.
        </p>
      </MedInfoButton>
    </template>

    <template #content>
      <template v-if="loading">
        <div class="py-10 text-center">
          <MedSpinner size="2xl" :data-test="getTestId('bmp-export-dialog-spinner')" />
        </div>
      </template>

      <template v-else-if="error">
        <ComponentAlert v-if="error === 'popup'" type="error" :test-id="getTestId('bmp-export-manager-dialog-popup')">
          <p>Ihr Browser scheint Popups zu blockieren, weshalb der BMP-Manager nicht geöffnet werden konnte.</p>
          <p>Bitte prüfen Sie Ihre "Pop-up"-Browser-Einstellungen unter "Datenschutz und Sicherheit".</p>
        </ComponentAlert>

        <ComponentAlert v-else type="error" :test-id="getTestId('bmp-export-manager-dialog')">
          Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
        </ComponentAlert>
      </template>

      <template v-else>
        <p class="text-sm">
          Möchten Sie folgende zusätzlichen Angaben aus der Eingabemaske für die Erstellung des BMP berücksichtigen?
        </p>

        <ul class="mt-4 space-y-4">
          <li>
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="splitSectionsBasedOnUsage"
                variant="primary"
                :data-test="getTestId('bmp-export-manager-dialog-split-section-usage-checkbox')"
              />
              <MedLabel>
                Generierung von Zwischenüberschriften und Sortierung der Artikel auf Basis der Medikationsart
              </MedLabel>
            </MedFormField>
          </li>
          <li>
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="prependMealToInfo"
                variant="primary"
                :data-test="getTestId('bmp-export-manager-dialog-prepend-meal-checkbox')"
              />
              <MedLabel>Angabe des Mahlzeitenzusammenhangs im Feld „Hinweise“</MedLabel>
            </MedFormField>
          </li>
        </ul>
      </template>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="loading"
        :data-test="getTestId('bmp-export-manager-dialog-push-to-bmp-button')"
        @click="pushToBmpManager"
      >
        BMP-Manager öffnen
      </MedButton>

      <MedButton :data-test="getTestId('bmp-export-manager-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
